import { lowerCase } from "lodash";
import { useMemo } from "react";
import { CategoryData } from "./types";
import { ProductData } from "./types";

interface SearchQuery {
    searchText: string,
    selectedCategory: CategoryData | null
}

interface SearchableProduct {
    keywords: string // Must be a lower case string with each word seperate by a space char
    product: ProductData[]
}

/**
 * Returns a lower case string containing every keyword for the product, seperated by spaces.
 */
const getProductKeywords = (product: ProductData, categories: CategoryData[]): string => {
    return [
        product.name,
        product.productId,
        product.tooltip,
        product.skuOptions.map((skuOption) => skuOption.skuNumber).join(' '),
        categories.filter(category => category.productIds.includes(product.productId)).map(category => category.name).join(' ')
    ].join(' ').toLocaleLowerCase();
}

/**
 * This hook's goal is to return fast and highly relevant search results for the given search query. A cached search
 * index and a keyword system are employeed to help achieve these goals.
 */
export const useSearchResults = (
    { searchText, selectedCategory }: SearchQuery,
    products: ProductData[],
    categories: CategoryData[]
) => {
    const searchableProducts = useMemo(() => {
        return products.map(product => ({
            keywords: getProductKeywords(product, categories),
            product: product
        }))
    }, [products]);

    let filteredProducts;
    if (searchText.length !== 0) {
        // Filter the products using searchText
        const searchWords = searchText.toLocaleLowerCase().split(' ');
        filteredProducts = searchableProducts.reduce((matchingProducts: ProductData[], searchableProduct) => {
            const matchingSearchWordCount = searchWords.reduce((matchingSearchWordCount: number, searchWord: string) => {
                return searchableProduct.keywords.includes(searchWord)
                    ? matchingSearchWordCount + 1
                    : matchingSearchWordCount;
            }, 0)
            if (matchingSearchWordCount !== searchWords.length) {
                return matchingProducts; // This product doesn't match
            }
            matchingProducts.push(searchableProduct.product); // This product matches
            return matchingProducts;
        }, []);
    } else if (selectedCategory !== null) {
        //Filter the products using the selected category
        filteredProducts = searchableProducts.reduce((matchingProducts: ProductData[], searchableProduct) => {
            if (!selectedCategory.productIds.includes(searchableProduct.product.productId)) {
                return matchingProducts; // This product doesn't match
            }
            matchingProducts.push(searchableProduct.product); // This product matches
            return matchingProducts;
        }, []);
    } else {
        filteredProducts = products;
    }

    return filteredProducts;
}